import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import {
  AlertTriangle,
  Clock3,
  ShieldAlert,
  CheckCircle,
  Filter,
  User,
} from 'lucide-react';

import { useAuth } from './AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Sheet, SheetContent } from './ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { TaskDetailsPanel } from './TaskDetailsPanel';
import { ROUTES } from '../src/routes/config';
import { getRenewalTasks, RenewalTask, RenewalTaskFilters } from '../src/Utils/renewalTaskApi';

type RiskClient = {
  clientId: string;
  clientName: string;
  overdue: number;
  critical: number;
  riskScore: number;
};

type StaffMetrics = {
  staffId: string;
  staffName: string;
  planned: number;
  completed: number;
  pending: number;
  total: number;
};

export const SupervisorDashboard: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [overdue, setOverdue] = useState(0);
  const [critical, setCritical] = useState(0);
  const [slaOnTime, setSlaOnTime] = useState(0);
  const [riskClients, setRiskClients] = useState<RiskClient[]>([]);
  const [highPriorityToday, setHighPriorityToday] = useState<RenewalTask[]>([]);
  const [dueThisWeek, setDueThisWeek] = useState<RenewalTask[]>([]);
  const [selectedTask, setSelectedTask] = useState<RenewalTask | null>(null);
  const [ownerOptions, setOwnerOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [staffMetrics, setStaffMetrics] = useState<StaffMetrics[]>([]);
  const [allTasks, setAllTasks] = useState<RenewalTask[]>([]);

  const [filters, setFilters] = useState<{
    bucket?: RenewalTaskFilters['bucket'];
    status?: RenewalTaskFilters['status'];
    ownerUserId?: string;
    range: NonNullable<RenewalTaskFilters['range']>;
  }>({
    range: 'week',
  });

  const weekInterval = useMemo(() => {
    const now = new Date();
    return {
      start: startOfWeek(now, { weekStartsOn: 1 }),
      end: endOfWeek(now, { weekStartsOn: 1 }),
    };
  }, []);

  const buildFilters = (extra: Partial<RenewalTaskFilters> = {}): RenewalTaskFilters => ({
    ...extra,
    ...(filters.ownerUserId ? { ownerUserId: filters.ownerUserId } : {}),
    ...(filters.bucket ? { bucket: filters.bucket } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.range ? { range: filters.range } : {}),
  });

  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      setLoading(true);
      setMetricsLoading(true);
      try {
        const safeLimit = 100;
        
        // Fetch all tasks for the selected range (for staff metrics and general data)
        const allTasksResponse = await getRenewalTasks(
          buildFilters({ range: filters.range, page: 1, limit: safeLimit }),
          token
        ).catch((err) => {
          console.warn('Failed to fetch all tasks:', err);
          return { success: false, data: { tasks: [] } };
        });
        
        const allTasksData = allTasksResponse?.success ? (allTasksResponse.data?.tasks || []) : [];
        setAllTasks(allTasksData);

        // Fetch specific metrics in parallel
        const [
          overdueRes,
          criticalRes,
          p0TodayRes,
          dueWeekRes,
        ] = await Promise.all([
          getRenewalTasks(buildFilters({ bucket: 'OVERDUE', range: filters.range, page: 1, limit: safeLimit }), token)
            .catch((err) => {
              console.warn('Failed to fetch overdue tasks:', err);
              return { success: false, data: { tasks: [], pagination: { totalCount: 0 } } };
            }),
          getRenewalTasks(buildFilters({ priority: 'P0', range: filters.range, page: 1, limit: safeLimit }), token)
            .catch((err) => {
              console.warn('Failed to fetch critical tasks:', err);
              return { success: false, data: { tasks: [], pagination: { totalCount: 0 } } };
            }),
          getRenewalTasks(buildFilters({ priority: 'P0', range: 'day', page: 1, limit: safeLimit }), token)
            .catch((err) => {
              console.warn('Failed to fetch P0 today tasks:', err);
              return { success: false, data: { tasks: [] } };
            }),
          getRenewalTasks(buildFilters({ range: 'week', page: 1, limit: safeLimit }), token)
            .catch((err) => {
              console.warn('Failed to fetch due week tasks:', err);
              return { success: false, data: { tasks: [] } };
            }),
        ]);

        const p0TodayTasks = p0TodayRes?.success ? (p0TodayRes.data?.tasks || []) : [];
        const dueWeekTasks = dueWeekRes?.success ? (dueWeekRes.data?.tasks || []) : [];

        // Calculate metrics
        const now = new Date();
        const overdueCount = overdueRes?.success ? (overdueRes.data?.pagination?.totalCount || overdueRes.data?.tasks?.length || 0) : 0;
        const criticalCount = criticalRes?.success ? (criticalRes.data?.pagination?.totalCount || criticalRes.data?.tasks?.length || 0) : 0;

        const slaEligible = allTasksData.filter((t) => t.dueDate && (t.status === 'CLOSED' || t.status === 'RESOLVED'));
        const slaOnTimeCount = slaEligible.filter(
          (t) => t.dueDate && t.updatedAt && new Date(t.updatedAt) <= new Date(t.dueDate)
        ).length;
        const slaOnTimeValue = slaEligible.length > 0 ? Math.round((slaOnTimeCount / slaEligible.length) * 100) : 0;

        setOverdue(overdueCount);
        setCritical(criticalCount);
        setSlaOnTime(slaOnTimeValue);

        setHighPriorityToday(p0TodayTasks);
        setDueThisWeek(
          dueWeekTasks.filter((t) =>
            t.dueDate ? isWithinInterval(new Date(t.dueDate), weekInterval) : false
          )
        );

        // Calculate staff-wise metrics
        const staffMap = new Map<string, StaffMetrics>();
        
        allTasksData.forEach((task) => {
          if (!task.owner?.id) return;
          
          const staffId = task.owner.id;
          if (!staffMap.has(staffId)) {
            const staffName = task.owner.firstName && task.owner.lastName
              ? `${task.owner.firstName} ${task.owner.lastName}`
              : task.owner.email || 'Unknown';
            staffMap.set(staffId, {
              staffId,
              staffName,
              planned: 0,
              completed: 0,
              pending: 0,
              total: 0,
            });
          }
          
          const metrics = staffMap.get(staffId)!;
          metrics.total += 1;
          
          if (task.status === 'CLOSED' || task.status === 'RESOLVED') {
            metrics.completed += 1;
          } else if (task.status === 'OPEN' || task.status === 'IN_PROGRESS' || task.status === 'WAITING') {
            metrics.pending += 1;
          }
          
          // Planned = total tasks assigned (all statuses)
          metrics.planned = metrics.total;
        });

        const staffMetricsArray = Array.from(staffMap.values()).sort((a, b) => b.total - a.total);
        setStaffMetrics(staffMetricsArray);

        // Risk clients snapshot
        const grouped = new Map<string, { clientName: string; overdue: number; critical: number }>();
        allTasksData.forEach((task) => {
          const key = task.clientId || task.clientName || 'unknown';
          if (!grouped.has(key)) {
            grouped.set(key, {
              clientName: task.clientName,
              overdue: 0,
              critical: 0,
            });
          }
          const entry = grouped.get(key)!;
          const isOverdue = task.bucket === 'OVERDUE' || (task.dueDate && new Date(task.dueDate) < now);
          if (isOverdue) entry.overdue += 1;
          if (task.priority === 'P0') entry.critical += 1;
        });

        const riskList: RiskClient[] = Array.from(grouped.entries()).map(([clientId, data]) => ({
          clientId,
          clientName: data.clientName,
          overdue: data.overdue,
          critical: data.critical,
          riskScore: data.overdue * 2 + data.critical * 3,
        }));

        setRiskClients(
          riskList
            .sort((a, b) => b.riskScore - a.riskScore)
            .slice(0, 3)
        );

        // Owner options
        const owners = Array.from(
          new Map(
            allTasksData
              .filter((t) => t.owner?.id)
              .map((t) => [t.owner!.id, `${t.owner?.firstName || ''} ${t.owner?.lastName || ''}`.trim() || t.owner?.email || 'Unknown'])
          ).entries()
        ).map(([id, label]) => ({ id, label }));
        setOwnerOptions(owners);
      } catch (error) {
        console.error('Failed to load supervisor dashboard data', error);
        setOverdue(0);
        setCritical(0);
        setSlaOnTime(0);
        setHighPriorityToday([]);
        setDueThisWeek([]);
        setRiskClients([]);
        setStaffMetrics([]);
        setAllTasks([]);
      } finally {
        setLoading(false);
        setMetricsLoading(false);
      }
    };

    fetchData();
  }, [token, filters, weekInterval]);


  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Supervisor Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">
            Task performance and team metrics overview
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3 w-full">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </div>

            <div className="flex flex-col gap-1 min-w-[140px]">
              <Label className="text-xs text-gray-500">Due Bucket</Label>
              <Select
                value={filters.bucket || 'all'}
                onValueChange={(val) =>
                  setFilters((prev) => ({
                    ...prev,
                    bucket: val === 'all' ? undefined : (val as RenewalTaskFilters['bucket']),
                  }))
                }
              >
                <SelectTrigger className="h-9"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="OVERDUE">Overdue</SelectItem>
                  <SelectItem value="D0_3">Due 0-3 days</SelectItem>
                  <SelectItem value="D4_7">Due 4-7 days</SelectItem>
                  <SelectItem value="D8_14">Due 8-14 days</SelectItem>
                  <SelectItem value="D15_30">Due 15-30 days</SelectItem>
                  <SelectItem value="D31_90">Due 31-90 days</SelectItem>
                  <SelectItem value="D90_PLUS">Due 90+ days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1 min-w-[140px]">
              <Label className="text-xs text-gray-500">Status</Label>
              <Select
                value={filters.status || 'all'}
                onValueChange={(val) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: val === 'all' ? undefined : (val as RenewalTaskFilters['status']),
                  }))
                }
              >
                <SelectTrigger className="h-9"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="WAITING">Waiting</SelectItem>
                  <SelectItem value="RESOLVED">Resolved</SelectItem>
                  <SelectItem value="CLOSED">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1 min-w-[140px]">
              <Label className="text-xs text-gray-500">Staff</Label>
              <Select
                value={filters.ownerUserId || 'all'}
                onValueChange={(val) =>
                  setFilters((prev) => ({
                    ...prev,
                    ownerUserId: val === 'all' ? undefined : val,
                  }))
                }
              >
                <SelectTrigger className="h-9"><SelectValue placeholder="All staff" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All staff</SelectItem>
                  {ownerOptions.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1 min-w-[140px]">
              <Label className="text-xs text-gray-500">Time Range</Label>
              <Select
                value={filters.range}
                onValueChange={(val) => setFilters((prev) => ({ ...prev, range: val as NonNullable<RenewalTaskFilters['range']> }))}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Today</SelectItem>
                  <SelectItem value="week">This week</SelectItem>
                  <SelectItem value="month">This month</SelectItem>
                  <SelectItem value="quarter">This quarter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards - Streamlined */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Overdue Tasks</p>
                <p className="text-3xl font-bold">{metricsLoading ? '—' : overdue}</p>
                <p className="text-xs text-gray-500 mt-2">Tasks past due date</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Critical (P0)</p>
                <p className="text-3xl font-bold">{metricsLoading ? '—' : critical}</p>
                <p className="text-xs text-gray-500 mt-2">Highest priority tasks</p>
              </div>
              <ShieldAlert className="w-8 h-8 text-orange-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">SLA On-Time</p>
                <p className="text-3xl font-bold">{metricsLoading ? '—' : `${slaOnTime}%`}</p>
                <p className="text-xs text-gray-500 mt-2">Completed before due date</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* High Priority Today & Due This Week - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShieldAlert className="w-5 h-5 text-orange-600" />
              High Priority Today
            </CardTitle>
            <CardDescription>P0 tasks due today</CardDescription>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : highPriorityToday.length === 0 ? (
              <p className="text-sm text-gray-500">No high priority tasks today</p>
            ) : (
              <div className="space-y-2">
                {highPriorityToday.slice(0, 5).map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedTask(task)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{task.itemName}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {task.clientName} • Due {task.dueDate ? format(new Date(task.dueDate), 'MMM d') : '—'}
                      </p>
                    </div>
                    <Badge variant="destructive" className="ml-2">P0</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock3 className="w-5 h-5 text-blue-600" />
              Due This Week
            </CardTitle>
            <CardDescription>Tasks due before week end</CardDescription>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : dueThisWeek.length === 0 ? (
              <p className="text-sm text-gray-500">No tasks due this week</p>
            ) : (
              <div className="space-y-2">
                {dueThisWeek.slice(0, 5).map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedTask(task)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{task.itemName}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {task.clientName} • Due {task.dueDate ? format(new Date(task.dueDate), 'MMM d') : '—'}
                      </p>
                    </div>
                    <Badge variant="outline" className="ml-2">
                      {task.priority}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Staff-wise Tasks and Status Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Staff Tasks & Status
          </CardTitle>
          <CardDescription>Detailed breakdown by staff member</CardDescription>
        </CardHeader>
        <CardContent>
          {loading || metricsLoading ? (
            <p className="text-sm text-gray-500">Loading staff data...</p>
          ) : staffMetrics.length === 0 ? (
            <p className="text-sm text-gray-500">No staff data available for the selected period</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff Member</TableHead>
                    <TableHead className="text-center">Total Tasks</TableHead>
                    <TableHead className="text-center">Planned</TableHead>
                    <TableHead className="text-center">Completed</TableHead>
                    <TableHead className="text-center">Pending</TableHead>
                    <TableHead className="text-center">Completion Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffMetrics.map((staff) => {
                    const completionRate = staff.total > 0
                      ? Math.round((staff.completed / staff.total) * 100)
                      : 0;

                    return (
                      <TableRow
                        key={staff.staffId}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(ROUTES.staffTasksView(staff.staffId))}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            {staff.staffName}
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-semibold">{staff.total}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{staff.planned}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="default" className="bg-green-500">{staff.completed}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="border-amber-400 text-amber-700">
                            {staff.pending}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-green-500 h-2 rounded-full transition-all"
                                style={{ width: `${completionRate}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">{completionRate}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* High-Risk Clients */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              High-Risk Clients
            </CardTitle>
            <CardDescription>Top clients by overdue and critical items</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.supervisorRiskView)}>
            View all
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-500">Loading risk data...</p>
          ) : riskClients.length === 0 ? (
            <p className="text-sm text-gray-500">No risk signals detected</p>
          ) : (
            <div className="space-y-2">
              {riskClients.map((client) => (
                <div
                  key={client.clientId}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(ROUTES.clientDetailsView(client.clientId))}
                >
                  <div>
                    <p className="font-medium">{client.clientName || 'Unknown Client'}</p>
                    <p className="text-xs text-gray-500">
                      Risk score {client.riskScore} • Overdue {client.overdue} • Critical {client.critical}
                    </p>
                  </div>
                  <Badge variant="destructive" className="text-xs">
                    {client.riskScore >= 6 ? 'High' : client.riskScore >= 3 ? 'Medium' : 'Low'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
        <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto p-0">
          {selectedTask && (
            <TaskDetailsPanel
              task={selectedTask}
              onUpdate={() => setSelectedTask(null)}
              onClose={() => setSelectedTask(null)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
